<script lang="ts">
  import { push } from 'svelte-spa-router';
  import { onMount } from 'svelte';
  import { auth, userStore } from '../stores/auth.store';
  import { getMenuItemsByRole } from '../lib/menus';
  import { charityPendingConfirmation } from '../stores/charity-pending-confirmation.store';
  import { employerPendingActions } from '../stores/employer-pending-actions.store';
  import { adminPendingActions } from '../stores/admin-pending-actions.store';

  export let activePage: string = 'dashboard';
  export let mobileNavEnabled = false;
  export let mobileOpen = false;

  // Version info
  let versionInfo: { build_number?: string; commit_hash?: string } | null = null;

  onMount(async () => {
    try {
      const response = await fetch('/version.json');
      if (response.ok) {
        versionInfo = await response.json();
      }
    } catch {
      // Version info not available, silently ignore
    }
  });

  // Get user role for role-based navigation
  $: userRole = $userStore?.user_role?.name || 'User';
  $: charityBankMenuBadge = (() => {
    if (userRole !== 'Charity') return null;
    const charity = $userStore?.organization?.data as
      | {
          verified_bsb_no?: string;
          verified_acc_no?: string;
          verified_acc_name?: string;
          bank_details_status?: string;
        }
      | undefined;
    if (!charity) return null;
    const hasBank = !!(
      (charity.verified_bsb_no || '').trim() ||
      (charity.verified_acc_no || '').trim() ||
      (charity.verified_acc_name || '').trim()
    );
    if (!hasBank) return 'Not added';
    return charity.bank_details_status === 'verified' ? 'Verified' : 'Pending';
  })();

  // State for expand/collapse sections
  let navigationExpanded = true;
  let helpSupportExpanded = true;
  let expandedSections = new Set<string>();

  async function handleLogout(): Promise<void> {
    try {
      await auth.logout();
      
      // Navigate to home page
    } catch (err) {
      // Still redirect to home even if API call fails
      push('/');
    }
  }

  // Get navigation items based on role from separate menu files
  $: navigationItems = getMenuItemsByRole(userRole);

  // Expand collapsible sections (e.g. admin Payments / Manage) by default.
  // Re-seeds when the role (and thus the menu) changes — userRole is initially a
  // placeholder before the user store loads — but otherwise respects manual collapse.
  let sectionsInitializedForRole = '';
  $: if (userRole && userRole !== sectionsInitializedForRole && navigationItems && navigationItems.length) {
    expandedSections = new Set(
      navigationItems.filter((i) => i.isSection && i.children).map((i) => i.id)
    );
    sectionsInitializedForRole = userRole;
  }

  // Toggle functions
  function toggleNavigation(): void {
    navigationExpanded = !navigationExpanded;
  }

  function toggleHelpSupport(): void {
    helpSupportExpanded = !helpSupportExpanded;
  }

  function toggleSection(sectionId: string): void {
    if (expandedSections.has(sectionId)) {
      expandedSections.delete(sectionId);
    } else {
      expandedSections.add(sectionId);
    }
    expandedSections = new Set(expandedSections); // Trigger reactivity
  }

  function handleNavigation(href: string | undefined, itemId: string): void {
    if (!href) return;
    activePage = itemId;
    push(href);
  }

  function handleKeydown(event: KeyboardEvent, action: () => void): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  }
</script>

<aside class="sidebar" class:admin-nav={userRole === 'Admin'} class:mobile-nav-enabled={mobileNavEnabled} class:mobile-open={mobileOpen}>
  <div class="logo">
    {#if mobileNavEnabled && mobileOpen}
      <button
        type="button"
        class="mobile-close"
        aria-label="Close navigation menu"
        on:click={() => (mobileOpen = false)}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24" aria-hidden="true">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
        </svg>
      </button>
    {/if}
    <img src="/logo.svg" alt="LEAVE2GIVE" />
    <h1>LEAVE2GIVE</h1>
  </div>
  
  <nav>
    <div class="section">
      <ul class="menu-list" id="navigation-menu">
        {#each navigationItems as item (item.id)}
          {#if item.isSeparator}
            <li class="menu-separator" aria-hidden="true"></li>
          {:else if item.isSection && item.children}
            <!-- Expandable section with children -->
            <li class="section-item">
              <button
                class="section-header"
                on:click={() => toggleSection(item.id)}
                on:keydown={(e) => handleKeydown(e, () => toggleSection(item.id))}
                type="button"
                aria-expanded={expandedSections.has(item.id)}
                aria-controls="submenu-{item.id}"
              >
                <div class="section-header-content">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
                    <path d={item.icon || ''} />
                  </svg>
                  <span class="menu-label">{item.label}</span>
                </div>
                <svg
                  class="chevron"
                  class:expanded={expandedSections.has(item.id)}
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  width="16"
                  height="16"
                  aria-hidden="true"
                >
                  <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
                </svg>
              </button>

              {#if expandedSections.has(item.id)}
                <ul class="submenu" id="submenu-{item.id}">
                  {#each item.children as child (child.id)}
                    <li class={activePage === child.id ? 'active' : ''}>
                      <a
                        href={child.href}
                        on:click|preventDefault={() => handleNavigation(child.href, child.id)}
                        class:active={activePage === child.id}
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
                          <path d={child.icon || ''} />
                        </svg>
                        <span class="menu-label">{child.label}</span>
                        {#if child.id === 'pending-payouts' && ($adminPendingActions.pendingPayoutsCount > 0 || $adminPendingActions.pendingPayoutsValue > 0)}
                          <span class="menu-count-badge menu-count-badge-red" aria-label="{$adminPendingActions.pendingPayoutsCount} ready for payout">
                            {$adminPendingActions.pendingPayoutsCount}
                          </span>
                        {/if}
                        {#if child.id === 'pending-confirmation' && ($adminPendingActions.pendingConfirmationCount > 0 || $adminPendingActions.pendingConfirmationValue > 0)}
                          <span class="menu-count-badge" aria-label="{$adminPendingActions.pendingConfirmationCount} awaiting confirmation">
                            {$adminPendingActions.pendingConfirmationCount}
                          </span>
                        {/if}
                        {#if child.id === 'unconfirmed-bank-details' && $adminPendingActions.unconfirmedBankDetailsCount > 0}
                          <span class="menu-count-badge menu-count-badge-amber" aria-label="{$adminPendingActions.unconfirmedBankDetailsCount} unconfirmed bank details">
                            {$adminPendingActions.unconfirmedBankDetailsCount}
                          </span>
                        {/if}
                        {#if child.id === 'verification-charities' && $adminPendingActions.charitiesInvitedCount > 0}
                          <span class="menu-count-badge" aria-label="{$adminPendingActions.charitiesInvitedCount} charities pending registration">
                            {$adminPendingActions.charitiesInvitedCount}
                          </span>
                        {/if}
                        {#if child.id === 'verification-employers' && $adminPendingActions.employersInvitedCount > 0}
                          <span class="menu-count-badge" aria-label="{$adminPendingActions.employersInvitedCount} employers pending registration">
                            {$adminPendingActions.employersInvitedCount}
                          </span>
                        {/if}
                      </a>
                    </li>
                  {/each}
                </ul>
              {/if}
            </li>
          {:else}
            <!-- Regular menu item -->
            <li class={activePage === item.id ? 'active' : ''}>
              <a
                href={item.href}
                on:click|preventDefault={() => handleNavigation(item.href, item.id)}
                class:active={activePage === item.id}
                class:has-menu-status={item.id === 'charity-bank-account' && !!charityBankMenuBadge}
              >
                {#if item.id === 'charity-bank-account' && charityBankMenuBadge}
                  <div class="menu-item-stack">
                    <div class="menu-item-row">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24" aria-hidden="true">
                        <path d={item.icon || ''} />
                      </svg>
                      <span class="menu-label">{item.label}</span>
                    </div>
                    <span
                      class="menu-status-badge"
                      class:verified={charityBankMenuBadge === 'Verified'}
                      class:pending={charityBankMenuBadge === 'Pending'}
                      class:empty={charityBankMenuBadge === 'Not added'}
                    >
                      {charityBankMenuBadge}
                    </span>
                  </div>
                {:else}
                  <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24" aria-hidden="true">
                    <path d={item.icon || ''} />
                  </svg>
                  <span class="menu-label">{item.label}</span>
                  {#if item.id === 'pending-confirmation' && $charityPendingConfirmation.count > 0}
                    <span class="menu-count-badge" aria-label="{$charityPendingConfirmation.count} awaiting confirmation">
                      {$charityPendingConfirmation.count}
                    </span>
                  {/if}
                  {#if item.id === 'leave-requests' && $employerPendingActions.pendingRequestsCount > 0}
                    <span class="menu-count-badge menu-count-badge-amber" aria-label="{$employerPendingActions.pendingRequestsCount} pending requests">
                      {$employerPendingActions.pendingRequestsCount}
                    </span>
                  {/if}
                  {#if item.id === 'outstanding-invoices' && ($employerPendingActions.pendingPaymentsCount > 0 || $employerPendingActions.pendingPaymentsValue > 0)}
                    <span class="menu-count-badge menu-count-badge-red" aria-label="{$employerPendingActions.pendingPaymentsCount} pending payments">
                      {$employerPendingActions.pendingPaymentsCount}
                    </span>
                  {/if}
                  {#if item.id === 'pending-payouts' && ($adminPendingActions.pendingPayoutsCount > 0 || $adminPendingActions.pendingPayoutsValue > 0)}
                    <span class="menu-count-badge menu-count-badge-red" aria-label="{$adminPendingActions.pendingPayoutsCount} pending payouts">
                      {$adminPendingActions.pendingPayoutsCount}
                    </span>
                  {/if}
                  {#if item.id === 'pending-confirmation' && ($adminPendingActions.pendingConfirmationCount > 0 || $adminPendingActions.pendingConfirmationValue > 0)}
                    <span class="menu-count-badge" aria-label="{$adminPendingActions.pendingConfirmationCount} awaiting confirmation">
                      {$adminPendingActions.pendingConfirmationCount}
                    </span>
                  {/if}
                  {#if item.id === 'unconfirmed-bank-details' && $adminPendingActions.unconfirmedBankDetailsCount > 0}
                    <span class="menu-count-badge menu-count-badge-amber" aria-label="{$adminPendingActions.unconfirmedBankDetailsCount} unconfirmed bank details">
                      {$adminPendingActions.unconfirmedBankDetailsCount}
                    </span>
                  {/if}
                {/if}
              </a>
            </li>
          {/if}
        {/each}
      </ul>
    </div>
    
    <!-- <div class="section">
      <button 
        class="section-header" 
        on:click={toggleHelpSupport}
        on:keydown={(e) => handleKeydown(e, toggleHelpSupport)}
        type="button"
        aria-expanded={helpSupportExpanded}
        aria-controls="help-support-menu"
      >
        <h3>Help & Support</h3>
        <svg 
          class="chevron" 
          class:expanded={helpSupportExpanded}
          viewBox="0 0 24 24" 
          fill="currentColor" 
          width="16" 
          height="16"
          aria-hidden="true"
        >
          <path d="M7 10l5 5 5-5z"/>
        </svg>
      </button>
      {#if helpSupportExpanded}
        <ul class="menu-list" class:expanded={helpSupportExpanded} id="help-support-menu">
          <li class={activePage === 'help-center' ? 'active' : ''}>
            <a 
              href="/help-center" 
              on:click|preventDefault={() => handleNavigation('/help-center', 'help-center')}
              class:active={activePage === 'help-center'}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24" aria-hidden="true">
                <path d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z" />
              </svg>
              <span>Help & Center</span>
            </a>
          </li>
          <li class={activePage === 'settings' ? 'active' : ''}>
            <a 
              href="/settings" 
              on:click|preventDefault={() => handleNavigation('/settings', 'settings')}
              class:active={activePage === 'settings'}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24" aria-hidden="true">
                <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
              </svg>
              <span>Settings</span>
            </a>
          </li>
        </ul>
      {/if}
    </div> -->
  </nav>
  
  <div class="logout">
    <button 
      on:click={handleLogout}
      on:keydown={(e) => handleKeydown(e, handleLogout)}
      type="button"
    >
      <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24" aria-hidden="true">
        <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
      </svg>
      <span>Log Out</span>
    </button>
    {#if versionInfo?.build_number}
      <div class="version-info">
        v{versionInfo.build_number}{#if versionInfo.commit_hash} ({versionInfo.commit_hash}){/if}
      </div>
    {/if}
  </div>
</aside>

<style>
  .sidebar {
    --sidebar-width: 250px;
    width: var(--sidebar-width);
    height: 100vh;
    background-color: #ffffff;
    color: #374151;
    display: flex;
    flex-direction: column;
    padding: 1.5rem 0;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
    overflow-y: auto;
    border-right: 1px solid #e5e7eb;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1000;
  }

  .sidebar.admin-nav {
    --sidebar-width: 300px;
  }

  .sidebar.admin-nav .menu-label {
    white-space: nowrap;
    font-size: 0.8125rem;
  }

  .sidebar.admin-nav li a {
    padding: 0.625rem 0.875rem;
    gap: 0.625rem;
  }

  /* Submenu items sit slightly smaller/tighter so longer labels (e.g.
     "Unconfirmed Bank Details") fit on one line alongside their count badge. */
  .sidebar.admin-nav .submenu li a {
    padding: 0.45rem 1rem;
    gap: 0.5rem;
    min-height: 36px;
    width: 100%;
    box-sizing: border-box;
  }

  .sidebar.admin-nav .submenu .menu-label {
    font-size: 0.75rem;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sidebar.admin-nav .submenu .menu-count-badge {
    margin-left: auto;
    flex-shrink: 0;
  }

  .sidebar.admin-nav .submenu li a svg {
    width: 18px;
    height: 18px;
  }

  .logo {
    display: flex;
    align-items: center;
    padding: 0 1.5rem;
    margin-bottom: 1rem;
    position: relative;
  }

  .mobile-close {
    display: none;
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #6b7280;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 6px;
    min-width: 44px;
    min-height: 44px;
    align-items: center;
    justify-content: center;
  }

  .mobile-close:hover {
    background-color: #f3f4f6;
    color: #374151;
  }

  .mobile-close:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(240, 180, 41, 0.1);
  }

  .logo img {
    height: 36px;
    margin-right: 0.5rem;
  }

  .logo h1 {
    font-size: 1.25rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  .section {
    margin-bottom: 1.5rem;
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1.5rem;
    cursor: pointer;
    transition: background-color 0.2s;
    border-radius: 6px;
    margin: 0;
    background: none;
    border: none;
    width: 100%;
    text-align: left;
    color: #6b7280;
    font-family: inherit;
    font-size: 1rem;
    min-height: 44px;
    box-sizing: border-box;
  }

  .section-header:hover {
    background-color: #f3f4f6;
    color: #374151;
  }

  .section-header:focus {
    outline: none;
  }

  .section-header-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .section-item {
    margin-bottom: 0.25rem;
  }

  .submenu {
    list-style: none;
    padding: 0;
    margin: 0;
    padding-left: 1rem;
    overflow: hidden;
    animation: slideDown 0.3s ease;
  }

  .submenu li {
    margin-bottom: 0.125rem;
  }

  .submenu li a {
    display: flex;
    align-items: center;
    padding: 0.5rem 1.5rem;
    color: #6b7280;
    text-decoration: none;
    transition: all 0.2s;
    border-radius: 0;
    font-size: 0.875rem;
    gap: 0.75rem;
    min-height: 40px;
    line-height: 1.4;
  }

  .submenu li a:hover {
    background-color: #f3f4f6;
    color: #374151;
  }

  .submenu li a:focus {
    outline: none;
  }

  .submenu li.active a,
  .submenu li a.active {
    background-color: #121826;
    color: #ffffff;
    border-radius: 8px;
    font-weight: 500;
  }

  .submenu li.active svg,
  .submenu li a.active svg {
    color: #ffffff;
  }

  .chevron {
    transition: transform 0.3s ease;
    color: #9ca3af;
    flex-shrink: 0;
  }

  .chevron.expanded {
    transform: rotate(180deg);
  }

  .menu-list {
    list-style: none;
    padding: 0;
    margin: 0;
    overflow: hidden;
    animation: slideDown 0.3s ease;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      max-height: 0;
    }
    to {
      opacity: 1;
      max-height: 500px;
    }
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    margin-bottom: 0.25rem;
    padding: 0 0.5rem;
  }

  .menu-separator {
    height: 0;
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 0.25rem 1rem;
    padding: 0;
    min-height: 0;
    line-height: 0;
  }

  li a {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    color: #6b7280;
    text-decoration: none;
    transition: all 0.2s;
    border-radius: 0;
    min-height: 44px;
    line-height: 1.4;
    width: 100%;
    box-sizing: border-box;
  }

  li a.has-menu-status {
    align-items: flex-start;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }

  .menu-item-stack {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.3rem;
    flex: 1;
    min-width: 0;
  }

  .menu-item-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    min-width: 0;
  }

  .menu-item-row .menu-label {
    flex: 1;
  }

  .menu-label {
    flex: 1;
    min-width: 0;
    word-wrap: break-word;
    text-align: left;
  }

  .menu-count-badge {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.25rem;
    height: 1.25rem;
    padding: 0 0.375rem;
    border-radius: 9999px;
    background: #8b5cf6;
    color: white;
    font-size: 0.6875rem;
    font-weight: 700;
    line-height: 1;
    flex-shrink: 0;
  }

  li.active a .menu-count-badge {
    background: #6d28d9;
  }

  .menu-count-badge-amber {
    background: #f59e0b;
  }

  li.active a .menu-count-badge-amber {
    background: #d97706;
  }

  .menu-count-badge-red {
    background: #ef4444;
  }

  li.active a .menu-count-badge-red {
    background: #dc2626;
  }

  .menu-item-stack .menu-status-badge {
    margin-left: calc(24px + 0.75rem);
  }

  .menu-status-badge {
    flex-shrink: 0;
    padding: 0.15rem 0.5rem;
    border-radius: 9999px;
    font-size: 0.6875rem;
    font-weight: 500;
    line-height: 1.3;
    background: #f3f4f6;
    color: #4b5563;
  }

  .menu-status-badge.verified {
    background: #d1fae5;
    color: #065f46;
  }

  .menu-status-badge.pending {
    background: #fef3c7;
    color: #92400e;
  }

  .menu-status-badge.empty {
    background: #f3f4f6;
    color: #6b7280;
  }

  li.active a .menu-status-badge.verified,
  li a.active .menu-status-badge.verified {
    background: rgba(255, 255, 255, 0.2);
    color: #ecfdf5;
  }

  li.active a .menu-status-badge.pending,
  li a.active .menu-status-badge.pending {
    background: rgba(255, 255, 255, 0.2);
    color: #fef3c7;
  }

  li.active a .menu-status-badge.empty,
  li a.active .menu-status-badge.empty {
    background: rgba(255, 255, 255, 0.15);
    color: #e5e7eb;
  }

  li a:hover {
    background-color: #f3f4f6;
    color: #374151;
  }

  li a:focus {
    outline: none;
  }

  li.active a,
  li a.active {
    background-color: #121826;
    color: #ffffff;
    border-radius: 8px;
    font-weight: 500;
  }

  li.active svg,
  li a.active svg {
    color: #ffffff;
  }

  li svg {
    color: inherit;
    flex-shrink: 0;
  }

  .logout {
    margin-top: auto;
    padding: 0 1.5rem;
  }

  .logout button {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0.75rem;
    background: none;
    border: none;
    color: #6b7280;
    cursor: pointer;
    font-size: 1rem;
    text-align: left;
    transition: all 0.2s;
    border-radius: 6px;
  }

  .logout button:hover {
    color: #374151;
    background-color: #f3f4f6;
  }

  .logout button:focus {
    outline: none;
  }

  .logout svg {
    margin-right: 0.75rem;
  }

  .version-info {
    font-size: 0.7rem;
    color: #9ca3af;
    text-align: center;
    margin-top: 0.5rem;
    padding: 0 0.5rem;
  }

  nav {
    flex: 1;
  }

  /* Responsive behavior for mobile (non-Admin users) */
  @media (max-width: 768px) {
    .sidebar.mobile-nav-enabled {
      transform: translateX(-100%);
      transition: transform 0.3s ease;
    }

    .sidebar.mobile-nav-enabled.mobile-open {
      transform: translateX(0);
    }

    .sidebar.mobile-nav-enabled .mobile-close {
      display: flex;
    }
  }
</style> 